import { inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import { SOFTLINE_API_DELIVERY_NOTE_COMMISSIONING_STAGES } from '../delivery-note-correction.api';
import {
  CommissioningStage,
  CommissioningStagesRequest,
  CommissioningStagesResponse,
} from '../types/commissioning-stages';

/** The commissioning stages of the listed delivery notes, loaded per page like the icons. A failed call is
 *  not remembered, so the next page load asks again and the rows stay "unknown" meanwhile. */
@Injectable()
export class CommissioningStagesService {
  private readonly dataAccess = inject(ApiDataAccess);

  private readonly cache = signal<ReadonlyMap<string, readonly CommissioningStage[]>>(new Map());
  private readonly inFlight = new Map<string, Promise<void>>();

  /** `undefined` until the stages of that document are known. */
  get(documentId: string | null | undefined): readonly CommissioningStage[] | undefined {
    return documentId ? this.cache().get(documentId) : undefined;
  }

  async loadFor(documentIds: readonly string[]): Promise<void> {
    const cached = this.cache();
    const missing = documentIds.filter((id) => !!id && !cached.has(id) && !this.inFlight.has(id));
    if (missing.length === 0) return;

    const request: CommissioningStagesRequest = { documentIds: missing };
    const promise = this.dataAccess
      .create<CommissioningStagesRequest, CommissioningStagesResponse>(
        { path: SOFTLINE_API_DELIVERY_NOTE_COMMISSIONING_STAGES },
        request
      )
      .then((response) => {
        this.cache.update((map) => {
          const next = new Map(map);
          for (const entry of response?.documents ?? []) next.set(entry.documentId, Object.freeze([...(entry.stages ?? [])]));
          return next;
        });
      })
      .catch(() => undefined)
      .finally(() => {
        for (const id of missing) this.inFlight.delete(id);
      });

    for (const id of missing) this.inFlight.set(id, promise);
    await promise;
  }
}
